import React from "react";
import {
    DataGridPremium,
    GridToolbarContainer,
    GridToolbarExportContainer,
    GridCsvExportMenuItem,
    GridToolbarContainerProps,
    GridCsvExportOptions,
    GridToolbarColumnsButton,
    GridToolbarFilterButton,
    GridToolbarDensitySelector,
    DataGridPremiumProps,
    GridExcelExportMenuItem,
    useGridApiRef,
} from '@mui/x-data-grid-premium';
import Button, { ButtonProps } from '@mui/material/Button';
import { SxProps, Theme, useTheme } from "@mui/material";
import { useAppSelector } from "../../redux";
import { HasAccess } from "../../router/authorization";
import { PERMISSIONS } from "../../router/permission";
import { PlanUpgrade } from "../planUpgrade";
import { DeleteOutline, Save } from "@mui/icons-material";

interface ITablePreference {
    include: boolean;
    onPreferenceSave: () => void;
}

const csvOptions: GridCsvExportOptions = { delimiter: ';' };

const CustomExportButton = (props: ButtonProps) => {

    const { perm } = useAppSelector(store => store.auth.userDetails);

    const showExportBtn = React.useMemo(() => HasAccess(perm, PERMISSIONS.EXPORT_TO_EXCEL), [perm]);

    return <GridToolbarExportContainer {...props}>
        {showExportBtn && <>
            <GridCsvExportMenuItem options={csvOptions} />
            <GridExcelExportMenuItem />
        </>}
        {!showExportBtn && <PlanUpgrade />}
    </GridToolbarExportContainer>
};

const CustomToolbar = (props: GridToolbarContainerProps & {
    showDeleteBtn?: boolean;
    disableDeleteBtn?: boolean;
    onDeleteBtnClicked?: () => void;
    preferenceDetails?: ITablePreference
}) => {
    return <GridToolbarContainer >
        {props.showDeleteBtn && <Button startIcon={<DeleteOutline />} color="error" size="small" disabled={props.disableDeleteBtn} onClick={() => props?.onDeleteBtnClicked?.()}>
            Delete
        </Button>}
        <CustomExportButton />
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        {props.preferenceDetails?.include && <Button startIcon={<Save />} color="primary" size="small" onClick={() => props.preferenceDetails?.onPreferenceSave()}>
            Save Preference
        </Button>}
    </GridToolbarContainer>
};


const useStyles = (theme: Theme): { [key: string]: SxProps } => ({
    dataGrid: {
        '.MuiDataGrid-cell': {
            display: "flex",
            alignItems: "center"
        },

        '.MuiDataGrid-cell[title]': {
            display: "list-item",
        },
        '@media print': {
            '.MuiDataGrid-main': { color: 'rgba(0, 0, 0, 0.87)' },
        }
    },
});

interface CustomDataGridProps extends DataGridPremiumProps {
    id?: string,
    saveLocal?: boolean,
    showToolbar?: boolean,
    showDeleteBtn?: boolean,
    disableDeleteBtn?: boolean,
    onDeleteBtnClicked?: () => void,
    preferenceDetails?: ITablePreference,
}

const WithLocalGrid: React.FC<CustomDataGridProps> = ({ showToolbar = false, pagination = true, id = "grid", ...props }) => {

    const classes = useStyles(useTheme());

    const apiRef = useGridApiRef();
    const [columnState, setColumnState] = React.useState(null);

    const updateState = () => {
        try {
            const savedState = localStorage.getItem(`dataGridColumnState-${id}`);
            if (savedState) {
                setColumnState(JSON.parse(savedState));
            }
        } catch (e) {
            console.error("Error loading saved state:", e);
        }
    }

    // Load saved column state on mount
    React.useEffect(() => {
        updateState();
        // eslint-disable-next-line
    }, [id, props.loading]);

    React.useEffect(() => {
        updateState();
        // eslint-disable-next-line
    }, []);

    // Apply column state after the grid is initialized and data is loaded
    React.useEffect(() => {
        if (columnState && apiRef.current.getRowModels().size > 0) {
            apiRef.current.restoreState(columnState);
        }
        // eslint-disable-next-line
    }, [columnState, apiRef, props.loading]);

    const handleColumnChange = () => {
        if (apiRef.current?.exportState) {
            const currentState = apiRef.current?.exportState?.();
            const columnOnlyState = {
                columns: currentState.columns,
            };
            localStorage.setItem(`dataGridColumnState-${id}`, JSON.stringify(columnOnlyState));
        }
    };

    return (
        <DataGridPremium
            apiRef={apiRef}
            {...props}
            initialState={props.initialState}
            disableRowSelectionOnClick={true}
            pagination={pagination}
            sx={classes.dataGrid}
            slots={{ toolbar: showToolbar ? CustomToolbar : null }}
            pageSizeOptions={[10, 25, 50, 100, 150, 200]}
            onColumnOrderChange={handleColumnChange}
            onColumnWidthChange={handleColumnChange}
            onColumnVisibilityModelChange={handleColumnChange}
        />
    )
};

export const CustomDataGrid: React.FC<CustomDataGridProps> = ({
    id = "grid",
    saveLocal = true,
    pagination = true,
    showToolbar = false,
    showDeleteBtn = false,
    disableDeleteBtn = false,
    onDeleteBtnClicked = () => false,
    preferenceDetails,
    ...props
}) => {

    const classes = useStyles(useTheme());

    const toolbarProps = showToolbar
        ? {
            slotProps: {
                toolbar: {
                    showDeleteBtn,
                    disableDeleteBtn,
                    onDeleteBtnClicked,
                    preferenceDetails
                },
            },
        }
        : {};

    if (saveLocal) {
        return <WithLocalGrid
            {...props}
            showToolbar={showToolbar}
            pagination={pagination}
            id={id}
            slotProps={{
                ...props.slotProps,
                toolbar: {
                    ...props.slotProps?.toolbar,
                    ...toolbarProps.slotProps?.toolbar
                },
            }}
        />;
    }

    return (
        <DataGridPremium
            {...props}
            disableRowSelectionOnClick={true}
            pagination={pagination}
            sx={classes.dataGrid}
            slots={{ toolbar: showToolbar ? CustomToolbar : null }}
            slotProps={{ toolbar: { ...toolbarProps.slotProps?.toolbar } }}
            pageSizeOptions={[10, 25, 50, 100, 150, 200]}
        />
    )
};
